import React, { Fragment } from "react";
import { createPortal } from "react-dom";
import { Dialog, Transition } from "@headlessui/react";
import {
  HiOutlineCheck,
  HiOutlineExclamationTriangle,
  HiOutlineXMark,
} from "react-icons/hi2";
import CustomButton from "components/common/Button/CustomButton";
import { releaseBodyScrollLockAfterTransition } from "../../TimeSheetDetailsSection/ai/utils/bodyScrollLock";
import { getTimesheetConfirmSummaryRows } from "../../utils/timesheetConfirmDetails";
import useCareLabels from "hooks/useCareLabels";

const TimesheetApproveConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  confirming = false,
  scheduleMismatch = false,
  details,
}) => {
  const { careSector } = useCareLabels();
  const summaryRows = getTimesheetConfirmSummaryRows(careSector);

  const handleClose = () => {
    if (!confirming) onClose?.();
  };

  const title = scheduleMismatch
    ? "Approve timesheet — schedule mismatch"
    : "Approve timesheet?";

  const introText = details
    ? "You are about to approve this pending timesheet:"
    : "You are about to approve this timesheet.";

  const content = (
    <Transition
      show={isOpen}
      as={Fragment}
      afterLeave={releaseBodyScrollLockAfterTransition}
    >
      <Dialog as="div" className="relative z-[120]" open={isOpen} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/45 backdrop-blur-[3px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left shadow-[0_24px_60px_rgba(15,23,42,0.18)] ring-1 ring-black/5 transition-all">
                <div className="flex items-center justify-between gap-3 border-b border-[#E5E7EB] bg-gradient px-4 py-3 text-white sm:px-5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/15 ring-1 ring-inset ring-white/20">
                      {scheduleMismatch ? (
                        <HiOutlineExclamationTriangle className="h-4 w-4" />
                      ) : (
                        <HiOutlineCheck className="h-4 w-4" />
                      )}
                    </span>
                    <Dialog.Title
                      as="h2"
                      className="text-[15px] font-bold leading-tight sm:text-base"
                    >
                      {title}
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={confirming}
                    className="rounded-lg p-1 text-white/80 transition hover:bg-white/10 hover:text-white disabled:opacity-40"
                    aria-label="Close"
                  >
                    <HiOutlineXMark className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-4 py-4 sm:px-5 sm:py-5">
                  <p className="text-sm leading-relaxed text-[#64748B]">
                    {introText}
                  </p>

                  {details ? (
                    <div className="mt-3 overflow-hidden rounded-xl border border-[#E5E7EB] bg-[#F8FAFC]">
                      <div className="border-b border-[#E5E7EB] bg-[#EEF2FB]/70 px-4 py-2.5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#4A6BB6]">
                          Timesheet summary
                        </p>
                      </div>
                      <dl className="divide-y divide-[#E5E7EB]">
                        {summaryRows.map(({ key, label }) => (
                          <div
                            key={key}
                            className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4"
                          >
                            <dt className="text-xs font-semibold text-[#64748B]">
                              {label}
                            </dt>
                            <dd className="text-sm font-semibold text-[#0F172A] sm:max-w-[62%] sm:text-right">
                              {details?.[key] ?? "—"}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ) : null}

                  {scheduleMismatch ? (
                    <div className="mt-3 flex gap-3 rounded-xl border border-[#F5D78E] bg-[#FEF3C7] px-4 py-3">
                      <HiOutlineExclamationTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#D97706]" />
                      <p className="text-sm leading-relaxed text-[#92400E]">
                        The schedule time does not match with actual time. Confirm
                        only if you have reviewed both and still want to approve
                        this timesheet.
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className="border-t border-[#E5E7EB] bg-[#F8FAFC]/90 px-4 py-3 sm:px-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm font-medium text-[#0F172A]">
                      Do you want to continue?
                    </p>
                    <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                      <CustomButton
                        type="button"
                        variant="secondary"
                        label="No, go back"
                        clickHandler={handleClose}
                        isDisabled={confirming}
                        cssClass="!min-w-[7.5rem] justify-center"
                      />
                      <CustomButton
                        type="button"
                        variant="primary"
                        label={confirming ? "Approving…" : "Yes, approve"}
                        Icon={
                          !confirming ? (
                            <HiOutlineCheck className="h-4 w-4" />
                          ) : null
                        }
                        clickHandler={onConfirm}
                        isDisabled={confirming}
                        showLoading={confirming}
                        cssClass={`!min-w-[7.5rem] justify-center${
                          scheduleMismatch ? " !bg-[#D97706] hover:!opacity-95" : ""
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );

  return createPortal(content, document.body);
};

export default TimesheetApproveConfirmModal;
